// Merchant Service - GraphQL operations for Merchant role
import { graphqlClient } from "../graphqlClient";

// ==================== Profile ====================

export const getProfile = async () => {
  const query = `
    query GetMerchantProfile {
      merchantProfile {
        id
        email
        name
        status
        currency
        code
        balance
        apiKey
        secretKey
        callbackUrl
        webhookUrl
        depositCommission
        withdrawalCommission
        percentageFee
        parentName
        parentType
        parentDepositCommission
        parentWithdrawalCommission
        parentPercentageFee
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.merchantProfile;
};

export const updateProfile = async (input) => {
  const mutation = `
    mutation MerchantUpdateProfile($input: MerchantUpdateProfileInput!) {
      merchantUpdateProfile(input: $input) {
        id
        email
        name
        status
        currency
        code
        balance
        apiKey
        secretKey
        callbackUrl
        webhookUrl
        depositCommission
        withdrawalCommission
        percentageFee
        parentName
        parentType
        parentDepositCommission
        parentWithdrawalCommission
        parentPercentageFee
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.merchantUpdateProfile;
};

export const regenerateApiKeys = async () => {
  const mutation = `
    mutation MerchantRegenerateApiKeys {
      merchantRegenerateApiKeys {
        id
        email
        name
        status
        currency
        code
        balance
        apiKey
        secretKey
        callbackUrl
        webhookUrl
        depositCommission
        withdrawalCommission
        percentageFee
        parentName
        parentType
        parentDepositCommission
        parentWithdrawalCommission
        parentPercentageFee
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation);
  return result.merchantRegenerateApiKeys;
};

// ==================== Withdrawals ====================

export const createMerchantWithdrawal = async (form) => {
  const dto = {
    platformName: form.platformName,
    amount: form.amount,
    currency: "PKR",
    country: "PK",
    firstname: form.firstname,
    lastname: form.lastname,
    email: form.email,
    phone: form.phone,
    document_id: form.document_id,
    city: form.city,
    address: form.address,
    state: form.state,
    postcode: form.postcode,
    ipaddress: "127.0.0.1",
  };

  const mutation = `
    mutation CreateMerchantWithdrawal($dto: CreateJazzCashWithdrawalInput!) {
      createMerchantWithdrawal(dto: $dto) {
        transaction_id
        amount
        currency
        country
        status
        payout_type
        message
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { dto });
  return result.createMerchantWithdrawal;
};



// ==================== Dashboard Stats ====================

export const getDashboardStats = async (merchantId, startDate = null, endDate = null) => {
  const query = `
    query DashboardStats($merchantId: String!, $startDate: String, $endDate: String) {
      merchantpanelDashboardLiveMetrics(merchantId: $merchantId, startDate: $startDate, endDate: $endDate) {
        currentBalance
        totalDeposits
        totalWithdrawals
        successfulTxCount
        pendingTxCount
        declinedTxCount
      }
    }
  `;

  const result = await graphqlClient.query(query, { merchantId, startDate, endDate });
  return result.merchantpanelDashboardLiveMetrics;
};

// Transactions chart (deposits/withdrawals grouped time series)
export const getTransactionsChart = async (startDate, endDate, currency, merchantIds = null) => {
  const query = `
    query TransactionsChart($startDate: String!, $endDate: String!, $currency: String!, $merchantIds: [String!]) {
      transactionsChart(startDate: $startDate, endDate: $endDate, currency: $currency, merchantIds: $merchantIds) {
        name
        deposits
        withdrawals
      }
    }
  `;
  const result = await graphqlClient.query(query, { startDate, endDate, currency, merchantIds });
  return result.transactionsChart || [];
};

// ==================== Webhook Logs ====================

export const getMerchantWebhookLogs = async (page = 1, limit = 15) => {
  const query = `
    query MerchantWebhookLogs($page: Int, $limit: Int) {
      merchantWebhookLogs(page: $page, limit: $limit) {
        id
        paymentId
        transactionId
        event
        url
        httpStatus
        responseBody
        errorMessage
        requestPayload
        createdAt
        status
        direction
      }
    }
  `;

  const result = await graphqlClient.query(query, { page, limit });
  return result.merchantWebhookLogs;
};

export const getMerchantWebhookLogsCount = async () => {
  const query = `
    query MerchantWebhookLogsCount {
      merchantWebhookLogsCount
    }
  `;

  const result = await graphqlClient.query(query);
  return result.merchantWebhookLogsCount;
};

// ==================== Deposits (JazzCash / EasyPaisa) ====================

export const createMerchantDeposit = async (form) => {
  const dto = {
    platformName: form.platformName,
    amount: form.amount,
    currency: "PKR",
    country: "PK",
    firstname: form.firstname,
    lastname: form.lastname,
    email: form.email,
    phone: form.phone,
    city: form.city,
    address: form.address,
    state: form.state,
    postcode: form.postcode,
    return_url: "https://agent.panel.luxkgate.com/deposit/receipt",
    user_category: "Default",
    device_fingerprint: form.device_fingerprint || undefined,
    // reserved custom fields
    custom_field_1: null,
    custom_field_2: null,
    custom_field_3: null,
    custom_field_4: null,
    custom_field_5: null,
    custom_field_6: null,
  };

  const mutation = `
    mutation CreateMerchantDeposit($dto: CreateJazzCashDepositInput!) {
      createMerchantDeposit(dto: $dto) {
        transaction_id
        platformName
        amount
        currency
        country
        status
        message
        checkout_url
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { dto });
  return result.createMerchantDeposit;
};

// ==================== JazzCash / EasyPaisa Withdrawals ====================

export const createJazzCashWithdrawal = async (dto) => {
  const mutation = `
    mutation CreateMerchantWithdrawal($dto: CreateJazzCashWithdrawalInput!) {
      createMerchantWithdrawal(dto: $dto) {
        transaction_id
        merchant_ref_no
        payby
        amount
        currency
        country
        status
        payout_type
        message
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { dto });
  return result.createMerchantWithdrawal;
};

// ==================== Settlements ====================

export const getMerchantSettlements = async () => {
  const query = `
    query MerchantSettlements {
      merchantSettlements {
        id
        settlement_id
        merchant_id
        merchant_code
        period_start
        period_end
        total_deposits
        total_withdrawals
        total_fees
        net_amount
        status
        currency
        processed_at
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.merchantSettlements || [];
};
